import { useEffect } from "react";
import { isNativeRuntime } from "./platform";

/** Keep the screen on (POS / pay screens). Native: KeepAwake plugin; browser: Screen Wake Lock API when available. */
export function useKeepAwake(on = true) {
  useEffect(() => {
    if (!on) return;
    let release: (() => void) | null = null;
    let cancelled = false;
    if (isNativeRuntime()) {
      void import("@capacitor-community/keep-awake").then(({ KeepAwake }) => {
        if (cancelled) return;
        void KeepAwake.keepAwake().catch(() => undefined);
        release = () => void KeepAwake.allowSleep().catch(() => undefined);
      });
    } else if (typeof navigator !== "undefined" && "wakeLock" in navigator) {
      (navigator as Navigator & { wakeLock: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } }).wakeLock.request("screen")
        .then((l) => { if (cancelled) void l.release(); else release = () => void l.release().catch(() => undefined); }).catch(() => undefined);
    }
    return () => { cancelled = true; release?.(); };
  }, [on]);
}

/** Haptic "success" (charge taken). Native: Haptics plugin; browser: a short vibration where supported. */
export async function hapticSuccess() {
  if (isNativeRuntime()) {
    try { const { Haptics, NotificationType } = await import("@capacitor/haptics"); await Haptics.notification({ type: NotificationType.Success }); } catch { /* no vibrator */ }
    return;
  }
  try { navigator.vibrate?.(40); } catch { /* unsupported */ }
}

/** (Task P) light tap feedback on key actions (tabs, add to cart, clock in, open business). */
export async function hapticTap() {
  if (isNativeRuntime()) {
    try { const { Haptics, ImpactStyle } = await import("@capacitor/haptics"); await Haptics.impact({ style: ImpactStyle.Light }); } catch { /* no vibrator */ }
  }
}

/** Camera / photo library → JPEG data URL (native only). null = cancelled or not available (callers fall back to a file input). */
export async function capturePhoto(source: "camera" | "photos"): Promise<{ dataUrl: string } | { denied: true } | null> {
  if (!isNativeRuntime()) return null;
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  try {
    const p = await Camera.getPhoto({ source: source === "camera" ? CameraSource.Camera : CameraSource.Photos, resultType: CameraResultType.DataUrl, quality: 80, width: 1600, correctOrientation: true, saveToGallery: false });
    return p.dataUrl ? { dataUrl: p.dataUrl } : null;
  } catch (e) {
    const msg = String((e as Error)?.message ?? e);
    if (/denied|permission/i.test(msg)) return { denied: true };
    return null; // user cancelled
  }
}
